import { API_BASE_URL } from "@/lib/api/client"

function assetUrl(path: string) {
  return `${API_BASE_URL}/api/assets/${path}`
}

function triggerDownload(blobUrl: string, filename: string) {
  const a = document.createElement("a")
  a.href = blobUrl
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(blobUrl), 100)
}

async function fetchBlob(url: string): Promise<Blob> {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`)
  return res.blob()
}

function slugify(label: string) {
  return label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "") || "output"
}

function extFromPath(assetPath: string): string {
  const pathname = assetPath.split("?")[0]
  const lastSegment = pathname.split("/").pop() ?? ""
  const dot = lastSegment.lastIndexOf(".")
  return dot > 0 ? lastSegment.slice(dot) : ""
}

async function downloadAsset(assetPath: string, name: string, defaultExt: string) {
  const ext = extFromPath(assetPath) || defaultExt
  const blob = await fetchBlob(assetUrl(assetPath))
  const url = URL.createObjectURL(blob)
  triggerDownload(url, `${name}${ext}`)
}

/**
 * Download the output of a completed node.
 * @param nodeOutput  The outputs dict from the execution store
 * @param nodeLabel   Human label used to name the downloaded file
 */
export async function downloadNodeOutput(
  nodeOutput: Record<string, unknown>,
  nodeLabel: string,
): Promise<void> {
  const name = slugify(nodeLabel)

  if (typeof nodeOutput.text === "string") {
    const blob = new Blob([nodeOutput.text], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    triggerDownload(url, `${name}.txt`)
    return
  }

  if (typeof nodeOutput.image === "string") {
    await downloadAsset(nodeOutput.image, name, ".png")
    return
  }

  if (typeof nodeOutput.video === "string") {
    await downloadAsset(nodeOutput.video, name, ".mp4")
    return
  }

  if (typeof nodeOutput.audio === "string") {
    await downloadAsset(nodeOutput.audio, name, ".mp3")
    return
  }

  console.warn("downloadNodeOutput: no downloadable key found in", nodeOutput)
}

/** Returns true if the output has at least one downloadable key. */
export function isDownloadable(nodeOutput: Record<string, unknown> | undefined): boolean {
  if (!nodeOutput) return false
  return (
    typeof nodeOutput.text === "string" ||
    typeof nodeOutput.image === "string" ||
    typeof nodeOutput.video === "string" ||
    typeof nodeOutput.audio === "string"
  )
}
